import { useState, useEffect } from 'react';
import dompurify from 'dompurify';
import {
	Modal,
	Typography,
	Row,
	Col,
	Spin,
	Button,
	Breadcrumb,
	Tag,
} from 'antd';
import { CALL_SNAPPY_API } from 'src/rewardsStore/RewardsStoreGraphQL';
import { AnimatedRewardCard } from 'src/rewardsStore/AnimatedRewardCard.jsx';

const BUDGET = { minBudget: 76, maxBudget: 100 };
const COUNTRY = 'US';

const checkOptionsObject = (obj = {}) => {
	const optionTypes = [
		'takeYourPick',
		'color',
		'size',
		'scent',
		'flavor',
		'beverageType',
	];
	let matchingIndex = null;
	optionTypes.forEach((optionType, index) => {
		if (obj?.[optionType]) {
			matchingIndex = index;
		}
	});
	if (obj?.[optionTypes[matchingIndex]]) {
		return obj[optionTypes[matchingIndex]];
	}
	return null;
};

const AddMilestoneRewardModal = ({
	open,
	onCancel,
	onSubmit,
	currentUser,
	client,
	loading = false,
}) => {
	const [collections, setCollections] = useState([]);
	const [collectionsLoading, setCollectionsLoading] = useState(false);
	const [productsByCollection, setProductsByCollection] = useState({});
	const [productsLoading, setProductsLoading] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState(null);
	const [wizardStep, setWizardStep] = useState('collections');
	const [activeProduct, setActiveProduct] = useState(null);
	const [selectedVariant, setSelectedVariant] = useState(null);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	useEffect(() => {
		if (open) {
			fetchSnappyProductData({});
		}
	}, [open]);

	const fetchSnappyProductData = async ({ collectionId = null }) => {
		const isProduct = !!collectionId;
		try {
			if (isProduct) {
				setProductsLoading(true);
			} else {
				setCollectionsLoading(true);
			}

			const queryParams = [
				{ key: 'companyId', value: 'true' },
				{ key: 'accountId', value: 'true' },
				{ key: 'fields', value: 'full' },
				{
					key: isProduct ? 'country' : 'countries',
					value: COUNTRY,
				},
				{
					key: isProduct ? 'maxBudget' : 'budget',
					value: BUDGET.maxBudget,
				},
			];

			if (isProduct) {
				queryParams.push({ key: 'collectionId', value: collectionId });
				queryParams.push({ key: 'minBudget', value: BUDGET.minBudget });
			} else {
				queryParams.push({ key: 'types', value: 'swag,custom' });
			}

			const variables = {
				companyId: currentUser.company.id,
				endpoint: isProduct ? 'products' : 'collections',
				queryParams,
				method: 'GET',
			};

			const { data } = await client.query({
				query: CALL_SNAPPY_API,
				variables,
				fetchPolicy: 'network-only',
			});

			if (data?.callSnappyApi) {
				try {
					const parsedResponse = JSON.parse(data.callSnappyApi);
					const results = parsedResponse?.results || [];
					if (results && Array.isArray(results)) {
						if (isProduct) {
							setProductsByCollection((prev) => ({
								...prev,
								[collectionId]: results,
							}));
						} else {
							setCollections(results);
						}
					} else if (!isProduct) {
						setCollections([]);
					}
				} catch (parseError) {
					console.error('Error parsing Snappy API response:', parseError);
					if (!isProduct) {
						setCollections([]);
					}
				}
			} else if (!isProduct) {
				setCollections([]);
			}
		} catch (error) {
			console.error(
				`Error fetching ${isProduct ? 'products' : 'collections'}:`,
				error
			);
			if (!isProduct) {
				setCollections([]);
			}
		} finally {
			if (isProduct) {
				setProductsLoading(false);
			} else {
				setCollectionsLoading(false);
			}
		}
	};

	const handleCollectionClick = (collection) => {
		setSelectedCollection(collection);
		setWizardStep('products');
		if (!productsByCollection[collection.id]) {
			fetchSnappyProductData({ collectionId: collection.id });
		}
	};

	const handleBackToCollections = () => {
		setWizardStep('collections');
		setSelectedCollection(null);
		setActiveProduct(null);
		setSelectedVariant(null);
		setSelectedImageIndex(0);
	};

	const handleBackToProducts = () => {
		setWizardStep('products');
		setActiveProduct(null);
		setSelectedVariant(null);
		setSelectedImageIndex(0);
	};

	const handleProductClick = (product) => {
		setActiveProduct(product);
		if (product.variants && product.variants.length > 0) {
			setSelectedVariant(product.variants[0]);
		}
		setSelectedImageIndex(0);
		setWizardStep('details');
	};

	const handleVariantChange = (variant) => {
		setSelectedVariant(variant);
		setSelectedImageIndex(0);
	};

	const handleCancel = () => {
		setCollections([]);
		setProductsByCollection({});
		setSelectedCollection(null);
		setWizardStep('collections');
		setActiveProduct(null);
		setSelectedVariant(null);
		setSelectedImageIndex(0);
		onCancel();
	};

	const handleAddReward = (variant) => {
		const rewardInput = {
			companyId: currentUser.companyId,
			name:
				variant.title +
				(checkOptionsObject(variant?.optionAttributes)
					? ' - ' + checkOptionsObject(variant?.optionAttributes)
					: ''),
			description: variant?.information?.description || '',
			amount: 0,
			image: variant?.mediaItems?.[0]?.src || '',
			active: true,
			rewardType: 'milestone',
			externalId: activeProduct.id,
			snappyProduct: {
				id: activeProduct.id,
				brand: activeProduct.brand || null,
				budget: BUDGET.maxBudget,
				category: activeProduct.category,
				country: COUNTRY,
				coverImage:
					activeProduct?.coverImage?.src ||
					variant?.mediaItems?.[0]?.src ||
					'',
				description: variant?.information?.description || '',
				mediaItems: activeProduct.mediaItems.map((mi) => mi.src),
				options: activeProduct.options || {},
				title: activeProduct.title,
				types: activeProduct.types || [],
				variants: activeProduct.variants.map((v) => ({
					id: v.id,
					budget: BUDGET.maxBudget,
					brand: v.brand || null,
					category: v.category,
					country: COUNTRY,
					description: v?.information?.description,
					optionAttributes: v.optionAttributes,
					mediaItems: v.mediaItems.map((mi) => mi.src),
					title: v.title,
					types: v.types || [],
				})),
			},
		};
		onSubmit(rewardInput);
	};

	const renderBreadcrumb = () => (
		<Breadcrumb
			style={{ marginBottom: 8, marginTop: 8, fontSize: 16, fontWeight: 700 }}
			separator=">"
		>
			<Breadcrumb.Item>
				{wizardStep !== 'collections' ? (
					<span
						onClick={handleBackToCollections}
						style={{ cursor: 'pointer', color: '#1890ff' }}
					>
						Collections
					</span>
				) : (
					'Collections'
				)}
			</Breadcrumb.Item>
			{(wizardStep === 'products' || wizardStep === 'details') && (
				<Breadcrumb.Item>
					{wizardStep === 'details' ? (
						<span
							onClick={handleBackToProducts}
							style={{ cursor: 'pointer', color: '#1890ff' }}
						>
							{selectedCollection?.name || 'Products'}
						</span>
					) : (
						selectedCollection?.name || 'Products'
					)}
				</Breadcrumb.Item>
			)}
			{wizardStep === 'details' && activeProduct && (
				<Breadcrumb.Item>
					{activeProduct.title || selectedVariant?.title || 'Product Details'}
				</Breadcrumb.Item>
			)}
		</Breadcrumb>
	);

	const renderProductDetails = () => {
		if (!activeProduct || !selectedVariant) return null;

		const hasMultipleImages =
			selectedVariant.mediaItems && selectedVariant.mediaItems.length > 1;
		const hasMultipleVariants =
			activeProduct.variants && activeProduct.variants.length > 1;
		const hasOptions = checkOptionsObject(activeProduct?.options);

		return (
			<Row gutter={24} style={{ height: '600px' }}>
				<Col span={12}>
					<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
						<div
							style={{
								flex: 1,
								marginBottom: hasMultipleImages ? 16 : 0,
								border: '1px solid #f0f0f0',
								borderRadius: '8px',
								overflow: 'hidden',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: '#fafafa',
							}}
						>
							{selectedVariant.mediaItems?.[selectedImageIndex] ? (
								<img
									src={selectedVariant.mediaItems[selectedImageIndex].src}
									alt={selectedVariant.title}
									style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
								/>
							) : (
								<div style={{ color: '#595959', textAlign: 'center' }}>No image available</div>
							)}
						</div>
						{hasMultipleImages && (
							<div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
								{selectedVariant.mediaItems.map((mediaItem, index) => (
									<div
										key={index}
										onClick={() => setSelectedImageIndex(index)}
										style={{
											width: 80,
											height: 80,
											border: selectedImageIndex === index ? '2px solid #1890ff' : '1px solid #f0f0f0',
											borderRadius: '6px',
											overflow: 'hidden',
											cursor: 'pointer',
											flexShrink: 0,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											backgroundColor: '#fafafa',
										}}
									>
										<img
											src={mediaItem.src}
											alt={`${selectedVariant.title} ${index + 1}`}
											style={{ width: '100%', height: '100%', objectFit: 'cover' }}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</Col>
				<Col span={12}>
					<div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
						<div>
							<Typography.Text style={{ fontSize: '18px', fontWeight: 900 }}>
								{selectedVariant?.title +
									(hasMultipleVariants
										? ` - ${checkOptionsObject(selectedVariant?.optionAttributes)}`
										: '')}
							</Typography.Text>
						</div>
						<div>
							<Typography.Text strong style={{ fontSize: '16px' }}>
								Category:
							</Typography.Text>
							<div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
								{selectedVariant?.category ? (
									selectedVariant.category.split(' / ').map((seg, i) => (
										<Tag
											key={i}
											color={['blue', 'green', 'orange', 'purple', 'cyan', 'magenta'][i % 6]}
											style={{ fontSize: '12px' }}
										>
											{seg.trim()}
										</Tag>
									))
								) : (
									<Typography.Text type="secondary" style={{ fontSize: '14px' }}>
										N/A
									</Typography.Text>
								)}
							</div>
						</div>
						<div>
							<Typography.Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 8 }}>
								Description:
							</Typography.Text>
							<div
								style={{
									fontSize: '14px',
									lineHeight: '1.6',
									maxHeight: '200px',
									overflowY: 'auto',
									border: '1px solid #f0f0f0',
									borderRadius: '6px',
									padding: '12px',
									backgroundColor: '#fafafa',
								}}
								className="rewards-store-description"
								dangerouslySetInnerHTML={{
									__html: dompurify.sanitize(
										selectedVariant.information?.description
											?.replace('<p><p>', '')
											?.replace('</p></p>', '') || 'No description available'
									),
								}}
							/>
						</div>
						<div>
							<Typography.Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 8 }}>
								Product Type:
							</Typography.Text>
							<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
								{selectedVariant.types?.includes('digital') && (
									<Tag color="blue" style={{ fontSize: '12px' }}>Digital</Tag>
								)}
								{selectedVariant.types?.includes('physical') && (
									<Tag color="magenta" style={{ fontSize: '12px' }}>Physical</Tag>
								)}
								{selectedVariant.types?.includes('on-demand-swag') && (
									<Tag color="purple" style={{ fontSize: '12px' }}>Swag</Tag>
								)}
								{(!selectedVariant.types || selectedVariant.types.length === 0) && (
									<Typography.Text type="secondary">No type specified</Typography.Text>
								)}
							</div>
						</div>
						{hasMultipleVariants && hasOptions && (
							<div>
								<Typography.Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 8 }}>
									Available Options:
								</Typography.Text>
								<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
									{activeProduct.variants.map((variant) => {
										const optionValue = checkOptionsObject(variant?.optionAttributes);
										const isSelected = selectedVariant.id === variant.id;
										return (
											<Button
												key={variant.id}
												type={isSelected ? 'primary' : 'default'}
												size="small"
												onClick={() => handleVariantChange(variant)}
												style={{ borderRadius: '16px', minWidth: '80px' }}
											>
												{optionValue}
											</Button>
										);
									})}
								</div>
							</div>
						)}
						<div style={{ marginTop: 'auto', paddingTop: 16 }}>
							<div style={{ display: 'flex', gap: 12 }}>
								<Button
									type="primary"
									size="large"
									style={{ flex: 1 }}
									loading={loading}
									onClick={() => handleAddReward(selectedVariant)}
								>
									{activeProduct.variants.length > 1 ? 'Add This Variant' : 'Add This Reward'}
								</Button>
								{activeProduct?.variants?.length > 1 && (
									<Button
										type="default"
										size="large"
										style={{ flex: 1 }}
										loading={loading}
										onClick={() => {
											activeProduct.variants.forEach((variant) => {
												handleAddReward(variant);
											});
										}}
									>
										Add All Variants
									</Button>
								)}
							</div>
						</div>
					</div>
				</Col>
			</Row>
		);
	};

	return (
		<Modal
			title="Add Milestone Reward"
			open={open}
			centered
			onCancel={handleCancel}
			width={1000}
			footer={null}
		>
			<div style={{ padding: 0 }}>
				{renderBreadcrumb()}
				<div
					style={{
						height: '655px',
						overflowY: 'auto',
						border: '1px solid #f0f0f0',
						borderRadius: '8px',
						padding: '16px',
					}}
				>
					{collectionsLoading ? (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
							<Spin size="large" />
						</div>
					) : wizardStep === 'collections' && collections.length > 0 ? (
						<Row gutter={[16, 16]}>
							{collections.map((result) => (
								<Col xs={24} sm={12} md={8} key={result.id}>
									<AnimatedRewardCard
										item={result}
										onClick={handleCollectionClick}
										type="collection"
									/>
								</Col>
							))}
						</Row>
					) : wizardStep === 'products' ? (
						productsLoading ? (
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100% - 60px)' }}>
								<Spin size="large" />
							</div>
						) : productsByCollection[selectedCollection?.id]?.length > 0 ? (
							<Row gutter={[16, 16]}>
								{productsByCollection[selectedCollection.id].map((product) => (
									<Col xs={24} sm={12} md={8} key={product.id}>
										<AnimatedRewardCard
											item={product}
											onClick={handleProductClick}
											type="product"
										/>
									</Col>
								))}
							</Row>
						) : (
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100% - 60px)', color: '#595959' }}>
								No Products Found
							</div>
						)
					) : wizardStep === 'details' ? (
						renderProductDetails()
					) : wizardStep === 'collections' ? (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#595959' }}>
							No Collections Found
						</div>
					) : null}
				</div>
			</div>
		</Modal>
	);
};

export default AddMilestoneRewardModal;
